import { LeadMagnetAction, LeadMagnetState, QuizAnswers, QuizQuestion } from '@/types/lead-magnet';

import { BACK_FROM_FIRST_QUESTION } from './constants';
import { findAnswerOption, selectFirstUnansweredIndex } from './scoring';

export const LEAD_MAGNET_INITIAL_STATE: LeadMagnetState = {
	stage: 'intro',
	currentIndex: 0,
	answers: {},
	submitStatus: 'idle',
	leadSubmitted: false,
	hydrated: false
};

const pickKnownAnswers = (questions: QuizQuestion[], answers: QuizAnswers): QuizAnswers =>
	questions.reduce<QuizAnswers>((acc, question) => {
		const value = answers?.[question.slug];

		return findAnswerOption(question, value) ? { ...acc, [question.slug]: value } : acc;
	}, {});

export const createLeadMagnetReducer =
	(questions: QuizQuestion[]) =>
	(state: LeadMagnetState, action: LeadMagnetAction): LeadMagnetState => {
		const lastIndex = Math.max(questions.length - 1, 0);
		const clampIndex = (index: number) => Math.min(Math.max(index, 0), lastIndex);

		switch (action.type) {
			case 'HYDRATE': {
				const { snapshot } = action;

				if (!snapshot) return { ...state, hydrated: true };

				return {
					...state,
					stage: snapshot.stage,
					currentIndex: clampIndex(snapshot.currentIndex),
					answers: pickKnownAnswers(questions, snapshot.answers),
					leadSubmitted: snapshot.leadSubmitted,
					hydrated: true
				};
			}

			case 'START':
				return {
					...state,
					stage: 'question',
					currentIndex: clampIndex(selectFirstUnansweredIndex(questions, state.answers))
				};

			case 'ANSWER': {
				const index = questions.findIndex(question => question.slug === action.slug);
				const question = questions[index];

				// Ignore unknown questions or answer values to keep persisted state valid.
				if (!question || !findAnswerOption(question, action.value)) return state;

				const answers = { ...state.answers, [action.slug]: action.value };
				const isLast = index >= lastIndex;

				return {
					...state,
					answers,
					stage: isLast ? 'lead' : 'question',
					currentIndex: isLast ? index : clampIndex(index + 1)
				};
			}

			case 'BACK': {
				if (state.stage === 'lead') return { ...state, stage: 'question', currentIndex: lastIndex };

				if (state.stage !== 'question') return state;

				if (state.currentIndex > 0) return { ...state, currentIndex: state.currentIndex - 1 };

				// `exit` is handled by the page; the reducer treats it as staying on Q1.
				return BACK_FROM_FIRST_QUESTION === 'intro' ? { ...state, stage: 'intro' } : state;
			}

			case 'SUBMIT_START':
				return { ...state, submitStatus: 'submitting' };

			case 'SUBMIT_SUCCESS':
				return {
					...state,
					submitStatus: 'success',
					leadSubmitted: true,
					stage: action.postSubmitAction === 'breakdown' ? 'breakdown' : state.stage
				};

			case 'SUBMIT_ERROR':
				return { ...state, submitStatus: 'error' };

			case 'RESET':
				return { ...LEAD_MAGNET_INITIAL_STATE, hydrated: true };

			default:
				return state;
		}
	};
